import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  X, 
  Home, 
  Sparkles, 
  Copy, 
  Share2,
  ArrowRight,
  CheckCircle
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  home: Home,
  sparkles: Sparkles,
  copy: Copy,
  share: Share2
};

export const OnboardingTour = ({ onClose }) => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [tips, setTips] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    fetchOnboardingData();
  }, []);

  const fetchOnboardingData = async () => {
    try {
      const [tipsRes, statusRes] = await Promise.all([
        axios.get(`${API}/onboarding/tips`),
        axios.get(`${API}/onboarding/status`, { headers: getAuthHeaders() })
      ]);
      setTips(tipsRes.data.tips);
      setStatus(statusRes.data);
    } catch (error) {
      console.error('Onboarding fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (currentStep < tips.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const saveDontShowPreference = () => {
    if (dontShowAgain) {
      localStorage.setItem('hideOnboardingTour', 'true');
    }
  };

  const handleClose = () => {
    saveDontShowPreference();
    onClose();
  };

  const handleComplete = async () => {
    try {
      saveDontShowPreference();
      await axios.post(`${API}/onboarding/complete`, {}, { headers: getAuthHeaders() });
      onClose();
    } catch (error) {
      console.error('Complete onboarding error:', error);
      onClose();
    }
  };

  const handleAction = (action) => {
    saveDontShowPreference();
    if (action) {
      onClose();
      navigate(action);
    } else {
      handleNext();
    }
  };

  if (loading) {
    return null;
  }

  const currentTip = tips[currentStep];
  const Icon = iconMap[currentTip?.icon] || Home;
  const progress = ((currentStep + 1) / tips.length) * 100;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
      <Card className="w-full max-w-md mx-4 shadow-2xl border-0" data-testid="onboarding-modal">
        <CardContent className="p-0">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary to-primary/80 text-white p-6 rounded-t-lg">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm opacity-80">
                Step {currentStep + 1} of {tips.length}
              </span>
              <button 
                onClick={handleClose}
                className="p-1 hover:bg-white/20 rounded-full transition-colors"
                data-testid="close-onboarding"
              >
                <X size={20} />
              </button>
            </div>
            <Progress value={progress} className="h-1 bg-white/30" />
          </div>

          {/* Content */}
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon className="h-8 w-8 text-accent" />
            </div>
            
            <h3 className="text-xl font-heading font-bold text-foreground mb-2">
              {currentTip?.title}
            </h3>
            <p className="text-muted-foreground mb-6">
              {currentTip?.description}
            </p>

            {/* Progress indicators */}
            <div className="flex justify-center gap-2 mb-6">
              {tips.map((_, index) => (
                <div 
                  key={index}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentStep 
                      ? 'bg-primary' 
                      : index < currentStep 
                        ? 'bg-primary/50' 
                        : 'bg-muted'
                  }`}
                />
              ))}
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              {currentStep > 0 && (
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="flex-1 rounded-full"
                >
                  Back
                </Button>
              )}
              <Button
                onClick={() => handleAction(currentTip?.action)}
                className="flex-1 rounded-full bg-accent hover:bg-accent/90 gap-2"
                data-testid="onboarding-next"
              >
                {currentStep === tips.length - 1 ? (
                  <>
                    <CheckCircle size={18} />
                    Get Started
                  </>
                ) : currentTip?.action ? (
                  <>
                    Try It Now
                    <ArrowRight size={18} />
                  </>
                ) : (
                  <>
                    Next
                    <ArrowRight size={18} />
                  </>
                )}
              </Button>
            </div>

            {currentStep < tips.length - 1 && (
              <button
                onClick={handleComplete}
                className="text-sm text-muted-foreground hover:text-foreground mt-4 transition-colors"
              >
                Skip tour
              </button>
            )}

            {/* Don't show again checkbox */}
            <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-muted">
              <Checkbox 
                id="dontShowAgain" 
                checked={dontShowAgain}
                onCheckedChange={setDontShowAgain}
                data-testid="dont-show-again-checkbox"
              />
              <label 
                htmlFor="dontShowAgain" 
                className="text-sm text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
              >
                Don't show this again
              </label>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OnboardingTour;
